import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { CurrencyProvider } from '@/context/CurrencyContext';
import { AppShell } from '@/components/shell/AppShell';
import { Loader } from '@/components/common/Feedback';
import Login from '@/pages/Login';
import OAuthCallback from '@/pages/OAuthCallback';
import TodaysWork from '@/pages/TodaysWork';
import Opportunities from '@/pages/Opportunities';
import Dashboard from '@/pages/Dashboard';
import DetectionInbox from '@/pages/DetectionInbox';
import Projects from '@/pages/Projects';
import ProjectDetail from '@/pages/ProjectDetail';
import ChangeOrders from '@/pages/ChangeOrders';
import ChangeOrderDetail from '@/pages/ChangeOrderDetail';
import Integrations from '@/pages/Integrations';
import CostDatabase from '@/pages/CostDatabase';
import Analytics from '@/pages/Analytics';
import Settings from '@/pages/Settings';
import Validation from '@/pages/Validation';
import FounderDashboard from '@/pages/FounderDashboard';
import '@/App.css';

const PAGE_TITLES = {
  '/': "Today's Work",
  '/opportunities': 'Opportunities',
  '/projects': 'Projects',
  '/dashboard': 'Command Center',
  '/detections': 'Detection Inbox',
  '/change-orders': 'Change Orders',
  '/analytics': 'Analytics & Reporting',
  '/validation': 'Validation',
  '/founder': 'Founder Analytics',
  '/cost-database': 'Cost Database',
  '/integrations': 'Integrations',
  '/settings': 'Settings',
};

const ShellWithTitle = () => {
  const location = useLocation();
  let title = PAGE_TITLES[location.pathname] || '';
  if (location.pathname.startsWith('/projects/')) title = 'Project Detail';
  if (location.pathname.startsWith('/change-orders/')) title = 'Change Order';
  return <AppShell title={title} />;
};

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CurrencyProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/auth/google" element={<OAuthCallback provider="google" />} />
              <Route path="/auth/microsoft" element={<OAuthCallback provider="microsoft" />} />
              <Route element={<Protected><ShellWithTitle /></Protected>}>
                <Route path="/" element={<TodaysWork />} />
                <Route path="/opportunities" element={<Opportunities />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/projects/:id" element={<ProjectDetail />} />
                {/* Legacy / founder-only screens (hidden from PM nav) */}
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/detections" element={<DetectionInbox />} />
                <Route path="/change-orders" element={<ChangeOrders />} />
                <Route path="/change-orders/:id" element={<ChangeOrderDetail />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/validation" element={<Validation />} />
                <Route path="/founder" element={<FounderDashboard />} />
                <Route path="/cost-database" element={<CostDatabase />} />
                <Route path="/integrations" element={<Integrations />} />
                <Route path="/settings" element={<Settings />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster position="top-right" richColors />
          </CurrencyProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
